<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { gsap } from 'gsap';

const props = defineProps({
  data: { type: Object, default: () => {} },
});

const serviceLeftRef = ref(null);
const serviceRightRef = ref(null);
const textRefLeft = ref(null);
const textRefRight = ref(null);
const maskRef = ref(null);

const currentIndex = ref(0);
const isVisible = ref(false);

const animations = computed(() => props.data?.items ?? []);
const texts = computed(() =>
  animations.value.map((a) => a?.title_1).filter(Boolean),
);
const rightText = computed(() =>
  animations.value.length ? animations.value[0]?.title_2 : '',
);
const currentTextLeft = computed(() =>
  texts.value.length
    ? texts.value[currentIndex.value % texts.value.length]
    : '',
);

let observer;
let tl;
let tlRepeat;

onMounted(() => {
  if (serviceLeftRef.value) {
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) isVisible.value = true;
      },
      { threshold: 0.7 },
    );
    observer.observe(serviceLeftRef.value);
  }
});

watch(isVisible, (visible) => {
  if (!visible) return;
  tl?.kill();
  tlRepeat?.kill();

  tl = gsap.timeline({});
  tlRepeat = gsap.timeline({ repeat: -1, repeatDelay: 0.7 });

  tl.set(serviceLeftRef.value, { scaleX: 0, rotation: 23, duration: 0 })
    .to(serviceLeftRef.value, {
      scaleX: 1,
      rotation: 23,
      duration: 0.5,
      ease: 'ease-in-out',
    })
    .to(
      serviceRightRef.value,
      { scaleX: 1, duration: 0.5, ease: 'ease-in-out' },
      '-=0.5',
    )
    .to(
      textRefLeft.value,
      { opacity: 1, duration: 1, ease: 'ease-in-out' },
      '-=0.5',
    )
    .to(
      textRefRight.value,
      { opacity: 1, duration: 1, ease: 'ease-in-out' },
      '-=1',
    );

  tlRepeat
    .fromTo(
      serviceLeftRef.value,
      { rotation: 23 },
      { rotation: 383, duration: 1.2, ease: 'ease-in-out' },
      '+=1',
    )
    .to(
      {},
      {
        duration: 1,
        onStart: () => {
          const len = texts.value.length || 1;
          currentIndex.value = (currentIndex.value + 1) % len;
        },
      },
      '-=0.7',
    );
});

onBeforeUnmount(() => {
  tl?.kill();
  tlRepeat?.kill();
  if (observer && serviceLeftRef.value)
    observer.unobserve(serviceLeftRef.value);
  observer = undefined;
});
</script>
<template>
  <section
    :id="data.id"
    class="section rotline"
    :class="[
      data?.background_color
        ? `rotline--${data.background_color} bg-${data.background_color}`
        : '',
      data?.spacing?.top ? `rotline--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `rotline--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div ref="maskRef" class="rotline__inner">
      <div ref="serviceLeftRef" class="rotline__left">
        <div class="rotline__left-wrap">
          <span
            ref="textRefLeft"
            class="rotline__left-text service-animation"
            >{{ currentTextLeft }}</span
          >
        </div>
      </div>
      <div ref="serviceRightRef" class="rotline__right">
        <div class="rotline__right-wrap">
          <span
            ref="textRefRight"
            class="rotline__right-text service-animation"
            >{{ rightText }}</span
          >
        </div>
      </div>
      <div class="rotline__circle"></div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.rotline {
  $parent: &;
  padding: 0;
  width: 100%;
  position: relative;
  &--gray {
    -webkit-mask-image: -webkit-linear-gradient(transparent, $gray 25%);
    mask-image: -webkit-linear-gradient(transparent, $gray 25%);
    #{$parent} {
      &__inner {
        mask-image: -webkit-linear-gradient($gray 90%, transparent);
        -webkit-mask-image: -webkit-linear-gradient($gray 90%, transparent);
      }
    }
  }
  &--white {
    -webkit-mask-image: -webkit-linear-gradient(transparent, $white 25%);
    mask-image: -webkit-linear-gradient(transparent, $white 25%);
    #{$parent} {
      &__inner {
        mask-image: -webkit-linear-gradient($white 90%, transparent);
        -webkit-mask-image: -webkit-linear-gradient($white 90%, transparent);
      }
    }
  }
  &--black {
    -webkit-mask-image: -webkit-linear-gradient(transparent, $black 25%);
    mask-image: -webkit-linear-gradient(transparent, $black 25%);
    #{$parent} {
      &__inner {
        mask-image: -webkit-linear-gradient($black 90%, transparent);
        -webkit-mask-image: -webkit-linear-gradient($black 90%, transparent);
      }
      &__left {
        &-wrap {
          background-color: $white;
        }
        &-text {
          color: $black;
        }
      }
      &__right {
        &-wrap {
          background-color: $white;
        }
        &-text {
          color: $black;
        }
      }
    }
  }
  &--top {
    &-zero {
      #{$parent} {
        &__inner {
          padding-top: 84px;
        }
      }
    }
    &-xl {
      #{$parent} {
        &__inner {
          padding-top: 140px;
        }
      }
    }
    &-lg {
      #{$parent} {
        &__inner {
          padding-top: 124px;
        }
      }
    }
    &-md {
      #{$parent} {
        &__inner {
          padding-top: 116px;
        }
      }
    }
    &-sm {
      #{$parent} {
        &__inner {
          padding-top: 108px;
        }
      }
    }
  }
  &--bottom {
    &-zero {
      #{$parent} {
        &__inner {
          padding-bottom: 84px;
        }
      }
    }
    &-xl {
      #{$parent} {
        &__inner {
          padding-bottom: 140px;
        }
      }
    }
    &-lg {
      #{$parent} {
        &__inner {
          padding-bottom: 124px;
        }
      }
    }
    &-md {
      #{$parent} {
        &__inner {
          padding-bottom: 116px;
        }
      }
    }
    &-sm {
      #{$parent} {
        &__inner {
          padding-bottom: 108px;
        }
      }
    }
  }
  &__inner {
    display: flex;
    position: relative;
    padding: 164px 0;
    @include media-breakpoint-up(sm) {
      padding: 304px 0 253px;
    }
    @include media-breakpoint-up(md) {
      padding: 425px 0 258px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 425);
      @include responsive-spacing(padding-bottom, 258);
    }
  }
  &__left {
    left: 0;
    min-width: 100%;
    position: relative;
    top: 0;
    transform: scaleX(0);
    z-index: 3;
    &-wrap {
      align-items: center;
      background-color: $surface-container-2;
      border-radius: 0 40px 40px 0;
      display: flex;
      justify-content: flex-end;
      height: 32px;
      left: -70px;
      padding: 6px 34px 6px 17px;
      position: relative;
      transform-origin: right;
      top: 0;
      width: calc(50% + 85px);
      z-index: 2;
      @include media-breakpoint-up(sm) {
        border-radius: 0 40px 40px 0;
        height: 70px;
        left: -51px;
        padding: 11px 72px 11px 11px;
      }
      @include media-breakpoint-up(md) {
        border-radius: 0 80px 80px 0;
        height: 115px;
        left: -149px;
        padding: 21px 119px 20px 31px;
        top: 2px;
        width: calc(50% + 200px);
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-right-radius, 80);
        @include responsive-spacing(border-bottom-right-radius, 80);
        @include responsive-spacing(padding-top, 21);
        @include responsive-spacing(padding-right, 119);
        @include responsive-spacing(padding-bottom, 20);
        @include responsive-spacing(padding-left, 31);
        @include responsive-spacing(height, 115);
        @include responsive-spacing(left, -149);
        @include responsive-spacing(top, 2);
        width: calc(50% + px-to-vw(200));
      }
      @include media-breakpoint-up(xxl) {
        @include responsive-spacing(left, -200);
      }
      @include media-breakpoint-up(xxxl) {
        @include responsive-spacing(left, -250);
      }
    }
    &-text {
      color: $white;
      letter-spacing: 0px;
      opacity: 0;
      text-transform: uppercase;
    }
  }
  &__right {
    min-width: 100%;
    position: relative;
    transform: scaleX(0);
    &-wrap {
      align-items: center;
      background-color: $surface-container-2;
      border-radius: 20px 0 0 20px;
      display: flex;
      height: 32px;
      padding: 6px 17px 6px 31px;
      position: absolute;
      right: 100%;
      top: 0;
      width: calc(50% + 16px);
      z-index: 1;
      @include media-breakpoint-up(sm) {
        border-radius: 40px 0 0 40px;
        height: 70px;
        padding: 11px 11px 11px 70px;
        width: calc(50% + 35px);
      }
      @include media-breakpoint-up(md) {
        border-radius: 80px 0 0 80px;
        height: 115px;
        padding: 21px 31px 20px 117px;
        width: calc(50% + 60px);
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-left-radius, 80);
        @include responsive-spacing(border-bottom-left-radius, 80);
        @include responsive-spacing(padding-top, 21);
        @include responsive-spacing(padding-right, 31);
        @include responsive-spacing(padding-bottom, 20);
        @include responsive-spacing(padding-left, 117);
        @include responsive-spacing(height, 115);
        width: calc(50% + px-to-vw(60));
      }
    }
    &-text {
      text-transform: uppercase;
      opacity: 0;
      color: $white;
      letter-spacing: 0px;
    }
  }
  &__circle {
    background: $primary;
    border-radius: 50%;
    height: 19px;
    left: -3px;
    margin: 0 auto;
    position: absolute;
    right: 0;
    top: calc(50% - 10px);
    width: 19px;
    z-index: 33;
    @include media-breakpoint-up(sm) {
      height: 42px;
      left: -8px;
      top: calc(50% + 5px);
      width: 42px;
    }
    @include media-breakpoint-up(md) {
      height: 68px;
      left: -15px;
      top: calc(50% + 49px);
      width: 68px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 68);
      @include responsive-spacing(left, -15);
      top: calc(50% + px-to-vw(49));
      @include responsive-spacing(width, 68);
    }
  }
}
</style>
